import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { ApiError, authApi } from '../lib/api';

type Mode = 'login' | 'signup' | 'forgot' | 'reset';

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<Mode>('login');
  const [notice, setNotice] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  /** Affiché quand la connexion échoue faute d'adresse confirmée. */
  const [needsVerification, setNeedsVerification] = useState(false);

  if (auth.principal) return <Navigate to="/" replace />;

  function switchMode(next: Mode) {
    setMode(next);
    setError('');
    setNotice('');
    setNeedsVerification(false);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    setNeedsVerification(false);
    try {
      await auth.login(email, password);
      navigate('/');
    } catch (err) {
      // 403 signifie identifiants corrects mais adresse non confirmée. Sans ce
      // cas distinct, la personne réessaierait son mot de passe indéfiniment
      // alors que le courriel d'activation dort dans ses indésirables.
      if (err instanceof ApiError && err.status === 403) {
        setNeedsVerification(true);
        setError("Votre adresse n'est pas encore confirmée.");
      } else {
        setError(err instanceof Error ? err.message : 'Connexion impossible');
      }
    } finally {
      setBusy(false);
    }
  }

  async function signup(event: FormEvent) {
    event.preventDefault();
    if (password !== confirmPassword) {
      setError('Les deux mots de passe ne correspondent pas.');
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await authApi.signup(email, password);
      setNotice(
        `Compte créé. Un lien d'activation vient d'être envoyé à ${email}. ` +
          "Ouvrez-le pour activer votre compte — pensez à regarder vos indésirables.",
      );
      setMode('login');
      setPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Inscription impossible');
    } finally {
      setBusy(false);
    }
  }

  async function resendVerification() {
    setBusy(true);
    setNotice('');
    try {
      await authApi.resendVerification(email);
    } catch {
      /* réponse identique quoi qu'il arrive */
    } finally {
      setNotice("Un nouveau lien d'activation vient d'être envoyé, si le compte existe.");
      setNeedsVerification(false);
      setBusy(false);
    }
  }

  async function askReset(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await authApi.requestReset(email);
      // Réponse identique que l'adresse existe ou non : le message ne doit pas
      // révéler quels comptes sont enregistrés.
      setNotice(
        'Si un compte existe pour cette adresse, un lien de réinitialisation a été envoyé.',
      );
      setMode('reset');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Demande impossible');
    } finally {
      setBusy(false);
    }
  }

  async function applyReset(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await authApi.confirmReset(resetToken.trim(), newPassword);
      setNotice('Mot de passe modifié. Vous pouvez vous connecter.');
      setPassword('');
      setMode('login');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Réinitialisation impossible');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-page">
      <section className="auth-card">
        <div className="auth-brand">
          <img
            src="/logo-eduversa-300.png"
            alt="EDUVERSA — Learn without boundaries"
            className="auth-logo"
          />
        </div>

        <h1>{mode === 'signup' ? 'Créer un compte' : 'Bienvenue'}</h1>
        <p>
          {mode === 'signup'
            ? 'Inscrivez-vous pour accéder aux cours, aux classes en direct et aux laboratoires.'
            : 'Connectez-vous à votre espace d’apprentissage ou institutionnel.'}
        </p>

        {notice && <div className="notice">{notice}</div>}

        {mode === 'login' && (
          <form onSubmit={submit}>
            <label>
              Email
              <input
                type="email"
                value={email}
                onChange={(e: any) => setEmail(e.target.value)}
                required
              />
            </label>
            <label>
              Mot de passe
              <input
                type="password"
                value={password}
                onChange={(e: any) => setPassword(e.target.value)}
                minLength={8}
                required
              />
            </label>
            {error && <div className="alert error">{error}</div>}
            {needsVerification && (
              <button type="button" className="button link" onClick={resendVerification} disabled={busy}>
                Renvoyer le lien d’activation
              </button>
            )}
            <button className="button primary" disabled={busy}>
              {busy ? 'Connexion…' : 'Se connecter'}
            </button>
            <button type="button" className="button ghost" onClick={() => switchMode('signup')}>
              Créer un compte
            </button>
            <button type="button" className="button ghost" onClick={() => switchMode('forgot')}>
              Mot de passe oublié ?
            </button>
          </form>
        )}

        {mode === 'signup' && (
          <form onSubmit={signup}>
            <label>
              Email
              <input
                type="email"
                value={email}
                onChange={(e: any) => setEmail(e.target.value)}
                required
              />
            </label>
            <label>
              Mot de passe
              <input
                type="password"
                value={password}
                onChange={(e: any) => setPassword(e.target.value)}
                minLength={8}
                required
              />
            </label>
            <label>
              Confirmer le mot de passe
              <input
                type="password"
                value={confirmPassword}
                onChange={(e: any) => setConfirmPassword(e.target.value)}
                minLength={8}
                required
              />
            </label>
            <small className="form-hint">
              Huit caractères minimum. Un lien d’activation sera envoyé à votre adresse.
            </small>
            {error && <div className="alert error">{error}</div>}
            <button className="button primary" disabled={busy}>
              {busy ? 'Création…' : 'Créer mon compte'}
            </button>
            <button type="button" className="button ghost" onClick={() => switchMode('login')}>
              J’ai déjà un compte
            </button>
          </form>
        )}

        {mode === 'forgot' && (
          <form onSubmit={askReset}>
            <p>Indiquez votre adresse : un lien de réinitialisation vous sera envoyé.</p>
            <label>
              Email
              <input
                type="email"
                value={email}
                onChange={(e: any) => setEmail(e.target.value)}
                required
              />
            </label>
            {error && <div className="alert error">{error}</div>}
            <button className="button primary" disabled={busy}>
              {busy ? 'Envoi…' : 'Envoyer le lien'}
            </button>
            <button type="button" className="button ghost" onClick={() => switchMode('login')}>
              Retour à la connexion
            </button>
          </form>
        )}

        {mode === 'reset' && (
          <form onSubmit={applyReset}>
            <p>Saisissez le code reçu et votre nouveau mot de passe.</p>
            <label>
              Code reçu
              <input
                value={resetToken}
                onChange={(e: any) => setResetToken(e.target.value)}
                required
              />
            </label>
            <label>
              Nouveau mot de passe
              <input
                type="password"
                value={newPassword}
                onChange={(e: any) => setNewPassword(e.target.value)}
                minLength={8}
                required
              />
            </label>
            {error && <div className="alert error">{error}</div>}
            <button className="button primary" disabled={busy}>
              {busy ? 'Application…' : 'Changer mon mot de passe'}
            </button>
            <button type="button" className="button ghost" onClick={() => switchMode('login')}>
              Retour à la connexion
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
